import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Expeditor, Order } from '../../../../services';
import style from './BulkPublishModal.module.scss';
import SealCheckIcon from '../../../../components/shared/icons/SealCheckIcon';
import CloseIconNew from '../../../../components/shared/icons/CloseIconNew';

const MODAL_STATE = {
  CONFIRM: 'confirm',
  LOADING: 'loading',
  SUCCESS: 'success',
};

const toYmd = (dateStr) => {
  if (!dateStr) return null;
  const m = moment(dateStr, ['MM/DD/YYYY HH:mm:ss', 'MM/DD/YYYY', 'YYYY-MM-DD'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const toTenderUtc = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.utc().format() : null;
};

const toTons = (weight) => {
  if (weight == null || weight === '') return null;
  const num = Number(weight);
  return Number.isFinite(num) ? String(num / 1000) : null;
};

const normalizeAddress = (s) => (s || '').trim().toLowerCase();

// Ищем сохранённый адрес по совпадению fullAddress (тип 0 — отправитель, тип 1 — получатель).
// При совпадении текста — приоритет адресу из того же города. Если не найдено — null.
const findUserAddressId = (addresses, fullAddress, cityId) => {
  const target = normalizeAddress(fullAddress);
  if (!target) return null;
  const matches = (addresses || []).filter((a) => normalizeAddress(a.fullAddress) === target);
  if (!matches.length) return null;
  const byCity = cityId ? matches.find((a) => a.cityId === cityId) : null;
  return (byCity || matches[0]).id ?? null;
};

const buildAddressList = (row, addressBook = { from: [], to: [] }) => [
  {
    userAddressId: findUserAddressId(addressBook.from, row.addressFrom, row.cityIdFrom),
    countryId: row.countryIdFrom ?? null,
    cityId: row.cityIdFrom ?? null,
    address: row.addressFrom ?? '',
    shippingDate: toYmd(row.shippingDate),
    shippingHourFrom: 0,
    shippingHourTo: 0,
    latLng: '',
    type: 0,
    step: 1,
    isInterimPoint: false,
    recipientName: row.senderName ?? '',
    recipientPhoneNumber: row.senderPhoneNumber ?? '',
    recipientEmail: '',
  },
  {
    userAddressId: findUserAddressId(addressBook.to, row.addressTo, row.cityIdTo),
    countryId: row.countryIdTo ?? null,
    cityId: row.cityIdTo ?? null,
    address: row.addressTo ?? '',
    shippingDate: null,
    shippingHourFrom: 0,
    shippingHourTo: 0,
    latLng: '',
    type: 1,
    step: 2,
    isInterimPoint: false,
    recipientName: row.recipientName ?? '',
    recipientPhoneNumber: row.recipientPhoneNumber ?? '',
    recipientEmail: '',
  },
];

const buildPayload = (row, userId, expeditorIds, addressBook) => {
  const resolvedCurrency = row.currency && row.currency !== 'all' ? row.currency : null;

  return {
  order: {
    userId,
    price: 0,
    algoPrice: 0,
    isAlgoPrice: false,
    netPrice: 0,
    countryIdFrom: row.countryIdFrom ?? null,
    countryNameFrom: row.countryFromName ?? '',
    cityNameFrom: row.cityFrom ?? '',
    cityFromName: row.cityFrom ?? '',
    cityIdFrom: row.cityIdFrom ?? null,
    addressFrom: row.addressFrom ?? '',
    shippingDate: toYmd(row.shippingDate),
    shippingHourFrom: 0,
    shippingHourTo: 0,
    recipientName: row.recipientName ?? '',
    recipientEmail: '',
    recipientPhoneNumber: row.recipientPhoneNumber ?? '',
    countryIdTo: row.countryIdTo ?? null,
    countryNameTo: row.countryToName ?? '',
    cityIdTo: row.cityIdTo ?? null,
    cityNameTo: row.cityTo ?? '',
    addressTo: row.addressTo ?? '',
    latLng: '',
    unloadingDate: null,
    unloadingHourFrom: 0,
    unloadingHourTo: 0,
    details: 'Нет описания',
    weight: toTons(row.weight),
    volume: null,
    pallet: null,
    unitMeasurement: 'ton',
    category: row.category ?? '',
    tnvedCode: row.tnvedCode ?? '',
    referenceNumber: row.referenceNumber ?? '',
    vehicleType: row.vehicleTypeValue ?? null,
    loadingTypeId: null,
    insuranceIncluded: false,
    cargoPrice: 0,
    insuranceSum: 0,
    copyCount: 1,
    isActualCities: true,
    expeditorId: null,
    addressList: buildAddressList(row, addressBook),
    isExistInterimPoint: false,
    distance: 0,
    isCommission: false,
    commission: 0,
    currency: row.currency || 'KZT',
    temperature: row.temperature ?? null,
    isWithoutExecutor: false,
    isImportedFromExcel: true,
    acceptedCurrency: resolvedCurrency
  },
  expeditors: expeditorIds,
  tenderDateEnd: toTenderUtc(row.tenderDateEnd),
  };
};

const BulkPublishModal = ({ rows, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state);

  const [modalState, setModalState] = useState(MODAL_STATE.CONFIRM);
  const [expeditorIds, setExpeditorIds] = useState([]);
  const [addressBook, setAddressBook] = useState({ from: [], to: [] });

  useEffect(() => {
    Expeditor.getPartners()
      .then((response) => {
        if (response.status === 200) {
          const ids = response.data
            .filter((p) => p.state === 'Confirmed' && p.companyName)
            .map((p) => p.expeditorCompanyId);
          setExpeditorIds(ids);
        }
      })
      .catch((err) => console.error('Не удалось загрузить партнёров:', err));
  }, []);

  useEffect(() => {
    Promise.all([
      Order.getUserAddress({ type: 0 }).then((r) => r.data).catch(() => []),
      Order.getUserAddress({ type: 1 }).then((r) => r.data).catch(() => []),
    ]).then(([from, to]) => {
      setAddressBook({ from: from || [], to: to || [] });
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    setModalState(MODAL_STATE.LOADING);

    const results = await Promise.allSettled(
      rows.map((row) => Order.createTender(buildPayload(row, user?.id, expeditorIds, addressBook)))
    );

    const failed = results.filter((r) => r.status === 'rejected');
    const succeeded = results.filter((r) => r.status === 'fulfilled');

    failed.forEach((f) => {
      const msg =
        f.reason?.message ||
        f.reason?.data?.message ||
        f.reason?.title ||
        t('bulkCreate.publish.createError');
      toast.error(msg);
    });

    if (succeeded.length > 0) {
      setModalState(MODAL_STATE.SUCCESS);
    } else {
      setModalState(MODAL_STATE.CONFIRM);
    }
  }, [rows, user?.id, expeditorIds, addressBook, t]);

  const handleGoToOrders = () => navigate('/main');

  return (
    <div className={style.overlay} onClick={onClose}>
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>

        <div className={style.topbar}>
          <button className={style.closeBtn} onClick={onClose} aria-label={t('bulkCreate.publish.close')}>
            <CloseIconNew fill="#212121" />
          </button>
        </div>

        {modalState === MODAL_STATE.CONFIRM && (
          <>
            <div className={style.body}>
              <div className={style.texts}>
                <p className={style.title}>{t('bulkCreate.publish.confirmTitle')}</p>
                <p className={style.subtitle}>
                  {t('bulkCreate.publish.confirmSubtitle')}
                </p>
              </div>
            </div>
            <div className={style.btns}>
              <button className={style.btnOutline} onClick={onClose}>
                {t('bulkCreate.publish.cancel')}
              </button>
              <button className={style.btnConfirm} onClick={handleConfirm}>
                {t('bulkCreate.publish.confirm')}
              </button>
            </div>
          </>
        )}

        {modalState === MODAL_STATE.LOADING && (
          <>
            <div className={style.body}>
              <div className={style.texts}>
                <p className={style.title}>{t('bulkCreate.publish.confirmTitle')}</p>
                <p className={style.subtitle}>
                  {t('bulkCreate.publish.confirmSubtitle')}
                </p>
              </div>
            </div>
            <div className={style.btns}>
              <button className={style.btnOutline} disabled>
                {t('bulkCreate.publish.cancel')}
              </button>
              <div className={style.btnLoading}>
                <span className={style.spinner} />
                {t('bulkCreate.publish.publishing')}
              </div>
            </div>
          </>
        )}

        {modalState === MODAL_STATE.SUCCESS && (
          <>
            <div className={style.successBody}>
              <SealCheckIcon />
              <p className={style.title}>{t('bulkCreate.publish.successTitle')}</p>
            </div>
            <button className={style.btnFull} onClick={handleGoToOrders}>
              {t('bulkCreate.publish.goToOrders')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkPublishModal;
